import {
  TrackEventArg,
  TrackEventArguments,
  TrackGoogleAnalyticsEventArg,
} from './types';
import { createConfigParametersRepository } from '../../configParametersRepository';
import { CUSTOM_PARAMS_STORAGE_KEY } from '../resources/constants';
import { CustomParams } from '../google-analytics/ga4Types';
import { createRuntimeGoogleState } from '../init/ga/state';
import { getTrackingPageFromUrl, routeOptions } from './index';
import { gaWrapper, gtagWrapper } from '../google-analytics/common';
import { getUserPropertiesFromDimensions } from '../google-analytics/ga4';
import { createFirebaseAdapter } from '../firebase/firebaseAdapter';
import { createFirebaseClient } from '../firebase/firebaseClient';
import { checkGaInit } from '../init/ga';
import { getPageLocation } from '../google-analytics/pages/utils';
import { getGa4GroupId } from '../google-analytics/utils';
import { trackUserProperties } from '../../configParametersRepository/storageClient/utils';
import { handleEventError } from './error';

const getStoredPageName = (): string | undefined => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const trackingStorageClient = getTrackingStorageClient();
  const storedCustomParams = trackingStorageClient.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  ) as CustomParams;
  return storedCustomParams?.page_name;
};

const getEventPage = (): string => {
  if (!routeOptions.matcher || !routeOptions.pageMap) {
    return getStoredPageName();
  }
  return getTrackingPageFromUrl(routeOptions);
};

export const trackEvent = async ({
  action,
  label,
  page,
  category = 'NOT_DEFINED',
  dimensionsAndParams = {},
}: Partial<TrackEventArguments>) => {
  if (typeof window === 'undefined') return;
  await checkGaInit();
  const { getGoogleState, queueEvent } = createRuntimeGoogleState();
  const { hasThePageBeenTracked } = getGoogleState();

  if (!hasThePageBeenTracked) {
    queueEvent({ action, label, page, category });
    return;
  }

  const options = {
    action,
    label,
    category,
    page: page || getEventPage(),
    dimensionsAndParams,
  };

  trackEventInUniversal(options);
  trackEventInGa4(options);
};

const trackEventInUniversal = ({
  page,
  category,
  action,
  label,
}: TrackGoogleAnalyticsEventArg): void => {
  if (page) {
    gaWrapper('send', 'event', category, action, label, {
      nonInteraction: true,
      page,
    });
  }
};

/**
 * Tracks a Google Analytics 4 event.
 * @param data
 */
export const trackEventInGa4 = (data: TrackEventArg) => {
  const {
    page: screen_name,
    category: event_category,
    action: event_action,
    label: event_label,
    dimensionsAndParams,
  } = data;
  handleEventError({
    action: event_action,
    label: event_label,
    page: screen_name,
    category: event_category,
  });
  if (!screen_name) return;
  const ga4GroupId = getGa4GroupId();
  const firebaseAdapter = createFirebaseAdapter();
  const firebaseClient = createFirebaseClient(firebaseAdapter);
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const { getTrackingStoredValue } = getTrackingStorageClient();
  const storedCustomParams = getTrackingStoredValue(CUSTOM_PARAMS_STORAGE_KEY);

  const customParams: CustomParams = {
    ...storedCustomParams,
    screen_name,
    event_category,
    event_action,
    event_label,
  };
  trackUserProperties(getUserPropertiesFromDimensions(dimensionsAndParams));
  firebaseClient.trackEvent(customParams);
  gtagWrapper('event', 'edo_event', {
    send_to: ga4GroupId,
    edo_timestamp: Date.now(),
    page_location: getPageLocation(screen_name),
    ...customParams,
  });
};
